// Task 5: render conditional questions directly under the question they depend
// on, instead of wherever their display_order happens to place them.
//
// Anchor rule:
//   - a legacy single-parent question anchors to `parent_id`;
//   - a `visibility_rule` question anchors to the REFERENCED question with the
//     greatest display_order. Every referenced answer must exist before the
//     dependent can become visible, so the latest reference is the only
//     position where the question can sit directly below satisfied context.
//
// Used by the vendor wizard AND the admin review list so the two orders cannot
// drift. Pure and structural, so both callers pass their own row shapes.
import type { VisibilityRule } from '@l2u/shared/progress';

type OrderableQuestion = {
  id: string;
  display_order: number;
  parent_id: string | null;
  visibility_rule: VisibilityRule | null;
};

/** Flatten every question id referenced by a (possibly nested) rule. */
function ruleRefs(rule: VisibilityRule, out: string[]): string[] {
  if ('op' in rule) {
    for (const child of rule.children) ruleRefs(child, out);
  } else {
    out.push(rule.q);
  }
  return out;
}

/**
 * The question this one should be rendered beneath, or null when it stands on
 * its own. `visibility_rule` wins over `parent_id`: the DB CHECK forbids both
 * being set, but if legacy data has them, the rule is what the completion
 * engine actually evaluates. Self-references and unknown ids are ignored.
 */
function anchorIdFor<Q extends OrderableQuestion>(q: Q, byId: Map<string, Q>): string | null {
  if (q.visibility_rule) {
    let anchor: Q | undefined;
    for (const ref of ruleRefs(q.visibility_rule, [])) {
      const target = byId.get(ref);
      if (!target || target.id === q.id) continue;
      if (!anchor || target.display_order > anchor.display_order) anchor = target;
    }
    return anchor?.id ?? null;
  }
  if (q.parent_id === null || q.parent_id === q.id) return null;
  return byId.has(q.parent_id) ? q.parent_id : null;
}

/**
 * Reorder so each dependent question follows its anchor immediately, with
 * chains kept contiguous. Questions with no (resolvable) anchor keep their
 * display_order position. Returns a new array; the input is not mutated.
 */
export function orderQuestionsByParent<Q extends OrderableQuestion>(questions: Q[]): Q[] {
  const sorted = [...questions].sort((a, b) => a.display_order - b.display_order);
  const byId = new Map(sorted.map((q) => [q.id, q]));

  // Bucket dependents under their anchor, preserving display_order within each
  // bucket (sorted is already in that order).
  const dependents = new Map<string, Q[]>();
  const roots: Q[] = [];
  for (const q of sorted) {
    const anchor = anchorIdFor(q, byId);
    if (anchor === null) {
      roots.push(q);
    } else {
      const list = dependents.get(anchor) ?? [];
      list.push(q);
      dependents.set(anchor, list);
    }
  }

  const out: Q[] = [];
  const seen = new Set<string>();
  const emit = (q: Q): void => {
    if (seen.has(q.id)) return;
    seen.add(q.id);
    out.push(q);
    for (const child of dependents.get(q.id) ?? []) emit(child);
  };
  for (const q of roots) emit(q);

  // Anchor cycles are rejected at write time; if legacy bad data contains one,
  // its members are reachable from no root. Append anything still unseen in
  // display_order so a question is never silently dropped from the form.
  for (const q of sorted) emit(q);

  return out;
}
